import { FeatureLayer } from "@evergis/sgis";
import { DomPainter } from "@evergis/sgis/es/painters/DomPainter/DomPainter";
import { useCallback, useEffect, useRef } from "react";
import { Map } from "@evergis/sgis/es/Map";
import { BasePolygonEditor } from "../../../../../../react-sgis/controls/baseControls/BasePolygonEditor";
import { editorPolygonSnapSymbol } from "../../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./useSelectionSymbols";

export const usePolygonEditor = (
  map: Map,
  painter: DomPainter,
  layer: FeatureLayer,
  onEditFinish,
): [BasePolygonEditor, (isActive: boolean) => void] => {
  const editorControl = useRef<BasePolygonEditor>();
  const symbols = useSelectionSymbols();

  const initEditMode = useCallback(() => {
    if (!editorControl.current) {
      editorControl.current = new BasePolygonEditor(map, painter, {
        isActive: false,
        activeLayer: layer,
        symbol: symbols[GeometrySelectionSymbolType.polygonEdit].clone(),
        snappingSymbol: editorPolygonSnapSymbol,
      });
      editorControl.current.featureDragAllowed = false;

      editorControl.current.on("edit", onEditFinish);
    }
  }, [onEditFinish, map, painter, layer]);

  const setActive = useCallback((isActive: boolean) => {
    editorControl.current.isActive = isActive;
  }, []);

  const offEvents = useCallback(() => {
    if (editorControl.current) {
      editorControl.current.off("edit");
    }
  }, []);

  const clearProperties = useCallback(() => {
    editorControl.current = null;
  }, []);

  useEffect(() => {
    initEditMode();

    return () => {
      offEvents();
      clearProperties();
    };
  }, []);

  useEffect(() => {
    if (editorControl.current) {
      offEvents();
      editorControl.current.on("edit", onEditFinish);
    }

    return () => {
      offEvents();
    };
  }, [onEditFinish]);

  return [editorControl.current, setActive];
};
